import z from 'zod';

interface translationMappings {
	addressRequired: string;
	cityRequired: string;
	zipCodeRequired: string;
	max30Chars: string;
	stateRequired: string;
}

export const addressForm = {
	address: {
		name: 'address',
		label: 'Address',
		schema: z.string().trim().min(1, 'Address is required'),
	},
	zipCode: {
		name: 'zipCode',
		label: 'Zip Code',
		schema: z.coerce.string().regex(/^\d{5}$|^\d{5}-\d{4}$/, 'Please enter valid US zip code'),
	},
	city: {
		name: 'city',
		label: 'City',
		schema: z
			.string()
			.trim()
			.min(1, 'City is required')
			.max(30, 'Max of 30 characters')
			.regex(/^[a-zA-Z]+([ '-][a-zA-Z]+)*$/, 'Please enter a valid city name')
			.refine((val) => {
				return (
					!/  |^[-']|[-']$/.test(val) && // No double spaces, no leading/trailing hyphens or apostrophes
					!/^\d+$/.test(val) && // Not just numbers
					!/^(.)\1+$/.test(val) && // No strings of entirely repeated characters
					!/(.)\1{2,}/i.test(val) // No 3+ consecutive repeated characters
				);
			}, 'Please enter a valid city name'),
	},
	state: {
		name: 'state',
		label: 'State',
		schema: z.string().regex(/^[a-z]+$/i, 'Valid State is required'),
	},
};

export const addressFormSchema = [
	{ name: addressForm.address.name, validation: addressForm.address.schema },
	{ name: addressForm.zipCode.name, validation: addressForm.zipCode.schema },
	{ name: addressForm.city.name, validation: addressForm.city.schema },
	{ name: addressForm.state.name, validation: addressForm.state.schema },
];

export default function generateAddressFormSchema(translations: translationMappings) {
	const addressFormConfiguration = {
		address: {
			name: 'address',
			label: 'Address',
			schema: z.string().trim().min(1, translations.addressRequired),
		},
		zipCode: {
			name: 'zipCode',
			label: 'Zip Code',
			labelKey: 'zipCode',
			schema: z
				.string()
				.trim()
				.min(1, translations.zipCodeRequired)
				.regex(/^\d{5}(-\d{4})?$/, 'Please enter a valid zip code')
				.refine((val) => {
					// Additional validation - check if it's not all zeros or sequential
					const fiveDigit = val.split('-')[0];
					return fiveDigit !== '00000' && fiveDigit !== '12345';
				}, 'Please enter a valid zip code'),
		},
		city: {
			name: 'city',
			label: 'City',
			labelKey: 'city',
			schema: z
				.string()
				.trim()
				.min(1, translations.cityRequired)
				.max(30, translations.max30Chars)
				.regex(/^[a-zA-Z]+([ '-][a-zA-Z]+)*$/, 'Please enter a valid city name')
				.refine((val) => {
					return (
						!/  |^[-']|[-']$/.test(val) && // No double spaces, no leading/trailing hyphens or apostrophes
						!/^\d+$/.test(val) && // Not just numbers
						!/^(.)\1+$/.test(val) && // No strings of entirely repeated characters
						!/(.)\1{2,}/i.test(val) // No 3+ consecutive repeated characters
					);
				}, 'Please enter a valid city name'),
		},
		state: {
			name: 'state',
			label: 'State',
			schema: z.string().regex(/^[a-z]+$/i, translations.stateRequired),
		},
	};

	return [
		{ name: addressFormConfiguration.address.name, validation: addressFormConfiguration.address.schema },
		{ name: addressFormConfiguration.zipCode.name, validation: addressFormConfiguration.zipCode.schema },
		{ name: addressFormConfiguration.city.name, validation: addressFormConfiguration.city.schema },
		{ name: addressFormConfiguration.state.name, validation: addressFormConfiguration.state.schema },
	];
}
